import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { InvoicesService } from '@/api';
import type { InvoiceRevision } from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { invoiceKeys } from './useInvoices';

// Query key factory for invoice revision caches. orgId is part of the key so a
// tenant switch can't serve another org's cached revisions.
export const invoiceRevisionKeys = {
  all: ['invoice-revisions'] as const,
  lists: () => [...invoiceRevisionKeys.all, 'list'] as const,
  list: (orgId: string | null, invoiceId: string | null) =>
    [...invoiceRevisionKeys.lists(), orgId, invoiceId] as const,
};

// mapDtoToRevision converts the generated (all-optional) DTO into the strict
// domain type, supplying safe defaults for fields the server always sends.
function mapDtoToRevision(dto: {
  id?: string;
  orgId?: string;
  invoiceId?: string;
  revision?: number;
  status?: string;
  totalAmount?: number;
  subsidyTotal?: number;
  figures?: unknown;
  pdfObjectKey?: string;
  reason?: string;
  createdBy?: string;
  createdAt?: string;
}): InvoiceRevision {
  return {
    id: dto.id ?? '',
    orgId: dto.orgId ?? '',
    invoiceId: dto.invoiceId ?? '',
    revision: dto.revision ?? 0,
    status: dto.status ?? '',
    totalAmount: dto.totalAmount ?? 0,
    subsidyTotal: dto.subsidyTotal,
    figures: (dto.figures ?? undefined) as Record<string, unknown> | undefined,
    pdfObjectKey: dto.pdfObjectKey,
    reason: dto.reason,
    createdBy: dto.createdBy ?? '',
    createdAt: dto.createdAt ?? '',
  };
}

/**
 * useInvoiceRevisions lists the immutable revision snapshots for an invoice,
 * newest first. Disabled until an invoiceId is provided.
 */
export function useInvoiceRevisions(invoiceId: string | null | undefined) {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: invoiceRevisionKeys.list(orgId ?? null, invoiceId ?? null),
    queryFn: async (): Promise<InvoiceRevision[]> => {
      const res = await InvoicesService.getApiV1InvoicesRevisions(invoiceId as string);
      return (res.data ?? []).map(mapDtoToRevision);
    },
    enabled: !!invoiceId,
  });
}

/**
 * useResendInvoice re-sends the current invoice revision and clears the
 * needs_resend flag. Invalidates the invoice and revision caches so the
 * needs-resend banner and revision history refresh. The server returns 409
 * (INVOICE_NOT_RESENDABLE) for a draft, surfaced via the error toast.
 */
export function useResendInvoice() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      return await InvoicesService.postApiV1InvoicesResend(invoiceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceRevisionKeys.all });
      toast.success(t`invoices.revisions.resendSuccess`);
    },
    onError: () => {
      // The error is surfaced to the user via the toast below; no console logging
      // (frontend standard forbids console.* — the mutation already captures it).
      toast.error(t`invoices.revisions.resendError`);
    },
  });
}
